/**
 * Studio UI primitives.
 *
 * V2 inlined every control's styling, which is why the screen drifted into a
 * scruffy wrapping row of forty half-aligned widgets. These components exist so
 * a control is declared by what it MEANS (a number with a unit and a hint, a
 * range with a live value, an assumption that must be labelled as one) rather
 * than by a style object, and so the settings can be grouped into sections that
 * line up.
 */
import { ReactNode, useState } from 'react'
import type { Provenance } from './types'

// ---------------------------------------------------------------------------
//  Provenance — an assumption has to look like an assumption
// ---------------------------------------------------------------------------

const PROV_LABEL: Record<Provenance, string> = {
  measured: 'measured',
  assumed: 'assumed',
  claude: 'Claude',
}
const PROV_TITLE: Record<Provenance, string> = {
  measured: 'Taken from observed data — not a guess.',
  assumed: 'An assumption. Shown as one everywhere, including the board paper, and the headline is quoted as a range because of it.',
  claude: 'Proposed by Claude during a tuning run. Every figure it quoted was computed by this engine, not invented.',
}

export function Chip({ kind }: { kind: Provenance }) {
  return (
    <span className={`stu-chip stu-chip--${kind}`} title={PROV_TITLE[kind]}>
      {PROV_LABEL[kind]}
    </span>
  )
}

// ---------------------------------------------------------------------------
//  Panel
// ---------------------------------------------------------------------------

export function Panel({
  title, sub, actions, children, collapsible, defaultOpen = true, flush, badge,
}: {
  title: string
  sub?: ReactNode
  actions?: ReactNode
  children: ReactNode
  collapsible?: boolean
  defaultOpen?: boolean
  flush?: boolean
  badge?: ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  const shown = collapsible ? open : true
  return (
    <section className={`stu-panel${shown ? '' : ' stu-panel--collapsed'}`}>
      <header className="stu-panel-head">
        {collapsible ? (
          <button type="button" className="stu-panel-toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            <span className="stu-caret">{open ? '▾' : '▸'}</span>
            <span className="stu-panel-title">{title}</span>
            {badge}
          </button>
        ) : (
          <>
            <span className="stu-panel-title">{title}</span>
            {badge}
          </>
        )}
        {sub && <span className="stu-panel-sub">{sub}</span>}
        {actions && <span className="stu-panel-actions">{actions}</span>}
      </header>
      {shown && <div className={`stu-panel-body${flush ? ' stu-panel-body--flush' : ''}`}>{children}</div>}
    </section>
  )
}

// ---------------------------------------------------------------------------
//  Fields
// ---------------------------------------------------------------------------

interface FieldProps {
  label: string
  value?: ReactNode
  hint?: ReactNode
  prov?: Provenance
  disabled?: boolean
  children: ReactNode
}

export function Field({ label, value, hint, prov, disabled, children }: FieldProps) {
  return (
    <label className={`stu-field${disabled ? ' stu-field--disabled' : ''}`}>
      <span className="stu-field-label">
        <span>{label}</span>
        {value != null && <span className="stu-field-value">{value}</span>}
        {prov && <Chip kind={prov} />}
      </span>
      {children}
      {hint && <span className="stu-field-hint">{hint}</span>}
    </label>
  )
}

export function RangeField({
  label, value, min, max, step, onChange, format, hint, prov, disabled,
}: {
  label: string
  value: number
  min: number
  max: number
  step?: number
  onChange: (v: number) => void
  format?: (v: number) => string
  hint?: ReactNode
  prov?: Provenance
  disabled?: boolean
}) {
  return (
    <Field label={label} value={format ? format(value) : value} hint={hint} prov={prov} disabled={disabled}>
      <input
        type="range"
        min={min}
        max={max}
        step={step ?? 1}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </Field>
  )
}

export function NumberField({
  label, value, onChange, min, max, step, unit, hint, prov, disabled, width,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  min?: number
  max?: number
  step?: number
  unit?: string
  hint?: ReactNode
  prov?: Provenance
  disabled?: boolean
  width?: number
}) {
  return (
    <Field label={label} value={unit} hint={hint} prov={prov} disabled={disabled}>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step ?? 1}
        disabled={disabled}
        style={width ? { width } : undefined}
        onChange={(e) => {
          const n = Number(e.target.value)
          if (!Number.isFinite(n)) return
          onChange(min != null ? Math.max(min, n) : n)
        }}
      />
    </Field>
  )
}

export function TimeField({
  label, value, onChange, hint, prov,
}: { label: string; value: string; onChange: (v: string) => void; hint?: ReactNode; prov?: Provenance }) {
  return (
    <Field label={label} hint={hint} prov={prov}>
      <input type="time" value={value} onChange={(e) => onChange(e.target.value)} />
    </Field>
  )
}

export function TextField({
  label, value, onChange, placeholder, hint, maxLength,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  hint?: ReactNode
  maxLength?: number
}) {
  return (
    <Field label={label} hint={hint}>
      <input type="text" value={value} placeholder={placeholder} maxLength={maxLength} onChange={(e) => onChange(e.target.value)} />
    </Field>
  )
}

export function SelectField<T extends string>({
  label, value, options, onChange, hint, prov, disabled,
}: {
  label: string
  value: T
  options: { value: T; label: string }[]
  onChange: (v: T) => void
  hint?: ReactNode
  prov?: Provenance
  disabled?: boolean
}) {
  return (
    <Field label={label} hint={hint} prov={prov} disabled={disabled}>
      <select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value as T)}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Field>
  )
}

export function Toggle({
  label, checked, onChange, hint, disabled, prov,
}: {
  label: ReactNode
  checked: boolean
  onChange: (v: boolean) => void
  hint?: ReactNode
  disabled?: boolean
  prov?: Provenance
}) {
  return (
    <label className={`stu-check${disabled ? ' stu-field--disabled' : ''}`}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={() => onChange(!checked)} />
      <span className="stu-check-text">
        <span>
          {label} {prov && <Chip kind={prov} />}
        </span>
        {hint && <span className="stu-field-hint">{hint}</span>}
      </span>
    </label>
  )
}

export const SubHead = ({ children }: { children: ReactNode }) => (
  <div className="stu-subhead">{children}</div>
)

// ---------------------------------------------------------------------------
//  Pills, tabs, buttons
// ---------------------------------------------------------------------------

export function PillGroup<T extends string>({
  value, options, onChange, title,
}: {
  value: T
  options: { value: T; label: string; title?: string; disabled?: boolean }[]
  onChange: (v: T) => void
  title?: string
}) {
  return (
    <span className="stu-pills" title={title}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className="stu-pill"
          aria-pressed={value === o.value}
          disabled={o.disabled}
          title={o.title}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </span>
  )
}

export interface TabDef<T extends string> {
  key: T
  label: string
  /** A count worth drawing attention to (conflicts, warnings). */
  badge?: number
  /** Show the badge in a quiet colour rather than red. */
  quiet?: boolean
  hidden?: boolean
}

export function Tabs<T extends string>({
  tabs, value, onChange,
}: { tabs: TabDef<T>[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="stu-tabs" role="tablist">
      {tabs.filter((t) => !t.hidden).map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          className="stu-tab"
          aria-selected={value === t.key}
          onClick={() => onChange(t.key)}
        >
          {t.label}
          {t.badge != null && t.badge > 0 && (
            <span className={`stu-tab-badge${t.quiet ? ' stu-tab-badge--quiet' : ''}`}>{t.badge}</span>
          )}
        </button>
      ))}
    </div>
  )
}

export function Btn({
  children, onClick, kind = 'ghost', disabled, title, small, type = 'button',
}: {
  children: ReactNode
  onClick?: () => void
  kind?: 'primary' | 'gold' | 'ghost' | 'quiet' | 'danger' | 'claude'
  disabled?: boolean
  title?: string
  small?: boolean
  type?: 'button' | 'submit'
}) {
  return (
    <button
      type={type}
      className={`stu-btn stu-btn--${kind}${small ? ' stu-btn--sm' : ''}`}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  )
}

// ---------------------------------------------------------------------------
//  Cards, callouts, modal
// ---------------------------------------------------------------------------

export function StatCard({
  label, value, sub, prev, tone, onClick, hero, loss,
}: {
  label: ReactNode
  value: ReactNode
  sub?: ReactNode
  prev?: ReactNode
  tone?: 'good' | 'bad' | 'warn'
  onClick?: () => void
  /** Solid-green headline card — the single figure a director reads. */
  hero?: boolean
  /** Flip the hero card to red when the number is negative. */
  loss?: boolean
}) {
  return (
    <div
      className={`stu-card${tone ? ` stu-card--${tone}` : ''}${hero ? ' stu-card--hero' : ''}${hero && loss ? ' stu-card--loss' : ''}${onClick ? ' stu-card--clickable' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
    >
      <div className="stu-card-label">{label}</div>
      <div className="stu-card-value">{value}</div>
      {sub && <div className="stu-card-sub">{sub}</div>}
      {prev && <div className="stu-card-prev">{prev}</div>}
    </div>
  )
}

export function Callout({
  kind, title, children,
}: { kind: 'info' | 'ok' | 'warn' | 'error' | 'claude'; title?: ReactNode; children?: ReactNode }) {
  return (
    <div className={`stu-callout stu-callout--${kind}`}>
      {title && <div className="stu-callout-title">{title}</div>}
      {children}
    </div>
  )
}

export function Modal({
  title, note, width = 720, onClose, children, footer,
}: {
  title: ReactNode
  note?: ReactNode
  width?: number
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <div className="stu-modal-back" onClick={onClose}>
      <div className="stu-modal stu" style={{ width }} onClick={(e) => e.stopPropagation()}>
        <div className="stu-modal-head">
          <span className="stu-modal-title">{title}</span>
          <button type="button" className="stu-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        {note && <p className="stu-modal-note">{note}</p>}
        {children}
        {footer}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
//  Settings drawer — the fix for "controls all over the place"
// ---------------------------------------------------------------------------

export interface SettingsSection {
  key: string
  label: string
  hidden?: boolean
  body: ReactNode
}

export function SettingsDrawer({
  sections, defaultOpen = false, actions,
}: { sections: SettingsSection[]; defaultOpen?: boolean; actions?: ReactNode }) {
  const visible = sections.filter((s) => !s.hidden)
  const [open, setOpen] = useState(defaultOpen)
  const [active, setActive] = useState(visible[0]?.key ?? '')
  const current = visible.find((s) => s.key === active) ?? visible[0]
  return (
    <section className={`stu-panel${open ? '' : ' stu-panel--collapsed'}`}>
      <header className="stu-panel-head">
        <button type="button" className="stu-panel-toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          <span className="stu-caret">{open ? '▾' : '▸'}</span>
          <span className="stu-panel-title">Model settings</span>
        </button>
        <span className="stu-panel-sub">
          {open
            ? 'Every assumption the model is running on, grouped. Assumed values are labelled.'
            : `${visible.length} groups — demand, operating window, interchange, fleet, costs`}
        </span>
        {actions && <span className="stu-panel-actions">{actions}</span>}
      </header>
      {open && (
        <>
          <div className="stu-settings-tabs" role="tablist">
            {visible.map((s) => (
              <button
                key={s.key}
                type="button"
                role="tab"
                className="stu-settings-tab"
                aria-selected={current?.key === s.key}
                onClick={() => setActive(s.key)}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="stu-panel-body">{current?.body}</div>
        </>
      )}
    </section>
  )
}
